import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import type { PaymentLinkFormatDefinition } from './format-definition';

/**
 * Payment-Link Link-Tester — Herkunfts-/Wirkungs-Badge (Spec 503, Phase 4).
 *
 * Unter „c=1" wird JEDER Parameter außer `sig` signiert (Handover §1) — die
 * Design-Dimension „signiert/frei" ist damit falsch geschnitten. Dieser Badge
 * zeigt daher immer „wird signiert" PLUS die korrigierte Body-Reach-Dimension
 * („erreicht den Facade-Body") in vier Fällen:
 *  - `systemField` → „→ {field}" (mappt auf ein Body-Feld),
 *  - `meta`        → „→ metadata.<x>",
 *  - `routing`     → `m`/`c`: „Pflicht, kein API-Body-Feld" (verwirrt sonst, weil
 *                   `m` die Merchant-Kennung IST),
 *  - `raw`         → „mitsigniert, erreicht die Facade nicht".
 * Im Teaser (`PENDING_FORMAT` → keine `bodyReachOf`) bleibt es beim neutralen
 * „Format offen"-Hinweis.
 */
export function BodyReachBadge({
  format,
  paramKey,
}: {
  format: PaymentLinkFormatDefinition;
  paramKey: string;
}) {
  const { t } = useTranslation('payment-link');
  const reach = format.available ? format.bodyReachOf?.(paramKey) : undefined;

  if (reach === undefined) {
    return (
      <Badge variant="outline" data-reach="unknown">
        {t('tester.badge.unknown')}
      </Badge>
    );
  }

  const field = format.bodyFieldOf?.(paramKey);
  let effect: string;
  if (reach === 'systemField' || reach === 'meta') {
    effect = t('tester.badge.reach.field', { field: field ?? '' });
  } else if (reach === 'routing') {
    effect = t('tester.badge.reach.routing');
  } else {
    effect = t('tester.badge.reach.raw');
  }

  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="default" data-reach="signed">
        {t('tester.badge.willBeSigned')}
      </Badge>
      <Badge
        variant={reach === 'raw' ? 'outline' : 'secondary'}
        data-reach={reach}
      >
        {effect}
      </Badge>
    </span>
  );
}
